import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useUpdateStream } from "@prequel/import-react";
import {
  Button,
  ButtonStyle,
  ConfirmModal,
  DetailCardSection,
  Modal,
  Status,
  StatusLight,
} from "@prequel/shared-components";

import { getErrorMessage } from "@/api/helpers";
import { useDeleteStream } from "@/api/import/streams";
import DangerZone from "@/components/DangerZone";
import { useStreamContext } from "@/pages/import/StreamsPage/StreamPage";
import { useToast } from "@/providers/ToastProvider";

const StreamAdmin = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { stream, refreshStream } = useStreamContext();
  const updateStream = useUpdateStream();
  const deleteStream = useDeleteStream();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOrchestrationModal, setShowOrchestrationModal] = useState(false);

  const onConfirmDelete = () => {
    setShowDeleteModal(false);
    deleteStream.mutate(stream.id);
  };

  const onConfirmOrchestration = async () => {
    setShowOrchestrationModal(false);
    try {
      await updateStream.updateStream(stream.id, { is_enabled: !stream.is_enabled });
      addToast({
        kind: "SUCCESS",
        message: stream.is_enabled ? "Orchestration disabled." : "Orchestration enabled.",
      });
      refreshStream();
    } catch (error) {
      addToast({ kind: "ERROR", message: getErrorMessage(error, "Failed to update orchestration.") });
    }
  };

  const dangerZoneActions = useMemo(
    () => [
      {
        label: "Delete stream",
        description:
          "Deleting this stream will stop all loads and remove its history. Records already delivered to the destination will remain. This action cannot be undone.",
        buttonText: "Delete stream",
        onClick: () => setShowDeleteModal(true),
      },
    ],
    [],
  );

  return (
    <>
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <ConfirmModal
          title="Delete stream"
          message="Are you sure you want to delete this stream? This action cannot be undone."
          onConfirm={onConfirmDelete}
          confirmButtonText="Delete"
          onCancel={() => setShowDeleteModal(false)}
        />
      </Modal>

      <Modal open={showOrchestrationModal} onClose={() => setShowOrchestrationModal(false)}>
        <ConfirmModal
          title={stream.is_enabled ? "Disable orchestration" : "Enable orchestration"}
          message={
            stream.is_enabled
              ? "Loads will no longer be initiated automatically after an extract."
              : "Loads will be initiated automatically after any extract."
          }
          onConfirm={onConfirmOrchestration}
          confirmButtonText={stream.is_enabled ? "Disable" : "Enable"}
          onCancel={() => setShowOrchestrationModal(false)}
        />
      </Modal>

      <DetailCardSection title="Edit stream">
        <p className="mb-3 text-sm text-stone-600">
          Update the stream&apos;s field mappings, destination, or transformation expressions.
        </p>
        <Button
          text="Edit stream"
          type={ButtonStyle.TERTIARY}
          onClick={() => navigate(`/import/streams/${stream.id}/edit`)}
        />
      </DetailCardSection>

      <DetailCardSection title="Automatic orchestration">
        <p className="mb-3 text-sm text-stone-600">
          The stream can be enabled for automatic load orchestration, or disabled for manual load scheduling. If
          enabled, loads will be automatically initiated after any extract.
        </p>
        <div className="flex items-center gap-3">
          <Button
            text="Edit orchestration"
            type={ButtonStyle.TERTIARY}
            onClick={() => setShowOrchestrationModal(true)}
            disabled={updateStream.isUpdating}
          />
          <span className="flex items-center gap-2 text-sm text-stone-800">
            <StatusLight status={stream.is_enabled ? Status.ACTIVE : Status.INACTIVE} />
            {stream.is_enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
      </DetailCardSection>

      <DangerZone actions={dangerZoneActions} type="card" />
    </>
  );
};

export default StreamAdmin;
